
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { clearExplicitSignOut, resetSessionActivity } from './logoutState';

// Detect unsubstituted Azure DevOps tokens — signals a broken pipeline/config.
// Log a clear warning so it shows up in UAT browser console diagnostics.
if (!msalConfig.auth.clientId) {
  console.error(
    '[MSAL] clientId is empty. The #{ClientId} token in config.js was not substituted by the Azure DevOps pipeline. ' +
    'Check that the "Replace Tokens" task targets the deployed config.js and that the pipeline variable "ClientId" is defined.'
  );
}
if (!msalConfig.auth.authority || msalConfig.auth.authority.endsWith('/login.microsoftonline.com/')) {
  console.error(
    '[MSAL] tenantId is empty. The #{tenantId} token in config.js was not substituted by the Azure DevOps pipeline. ' +
    'Check that the pipeline variable "tenantId" is defined.'
  );
}

/**
 * Singleton PublicClientApplication instance shared across the app.
 *
 * Security: PublicClientApplication uses Auth Code + PKCE by default for SPAs.
 * The instance must be initialized (see initializeMsal) before React mounts
 * to correctly handle redirect responses.
 */
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Registers a callback to automatically set the active account after a
 * successful login, token acquisition, or silent SSO event.
 * This ensures acquireTokenSilent picks the correct account on subsequent calls.
 */
msalInstance.addEventCallback((event) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    event.payload?.account
  ) {
    msalInstance.setActiveAccount(event.payload.account);
  }
});

/**
 * Initializes MSAL and handles any pending redirect response.
 * Must be awaited once before ReactDOM.render / createRoot.
 *
 * @returns {Promise<void>}
 */
export async function initializeMsal() {
  await msalInstance.initialize();

  // Process the redirect response (sets tokens in the cache)
  const redirectResult = await msalInstance.handleRedirectPromise().catch((error) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[MSAL] handleRedirectPromise error:', error);
    }
    return null;
  });
  if (redirectResult?.account) {
    clearExplicitSignOut();
    resetSessionActivity();
  }

  // Strip OAuth redirect params from the URL after MSAL has processed them,
  // so users only see the clean origin URL (e.g. https://gccstudio.uat.accenture.com/).
  const oauthParams = ['code', 'state', 'session_state', 'client_info', 'error', 'error_description'];
  const hasHashParams = window.location.hash && oauthParams.some(p => window.location.hash.includes(p + '='));
  const hasQueryParams = window.location.search && oauthParams.some(p => new URLSearchParams(window.location.search).has(p));
  if (hasHashParams || hasQueryParams) {
    window.history.replaceState(null, '', window.location.pathname);
  }

  // Default active account to the first cached account if none is set
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }
}

export default msalInstance;
